/**
 * Copyright (c) 2018-2022 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Color, ColorMap } from '../../mol-util/color/index.js';
import { StructureElement, Bond } from '../../mol-model/structure.js';
import { MoleculeType } from '../../mol-model/structure/model/types.js';
import { getElementMoleculeType } from '../../mol-model/structure/util.js';
import { ParamDefinition as PD } from '../../mol-util/param-definition.js';
import { TableLegend } from '../../mol-util/legend.js';
import { getAdjustedColorMap } from '../../mol-util/color/color.js';
import { getColorMapParams } from '../../mol-util/color/params.js';
import { ColorThemeCategory } from './categories.js';
export const MoleculeTypeColors = ColorMap({
    water: 0x386cb0,
    ion: 0xf0027f,
    protein: 0xbeaed4,
    RNA: 0xfdc086,
    DNA: 0xbf5b17,
    PNA: 0x42A49A,
    saccharide: 0x7fc97f,
});
const DefaultMoleculeTypeColor = Color(0xffff99);
const Description = 'Assigns a color based on the molecule type of a residue.';
export const MoleculeTypeColorThemeParams = {
    saturation: PD.Numeric(0, { min: -6, max: 6, step: 0.1 }),
    lightness: PD.Numeric(0, { min: -6, max: 6, step: 0.1 }),
    colors: PD.MappedStatic('default', {
        'default': PD.EmptyGroup(),
        'custom': PD.Group(getColorMapParams(MoleculeTypeColors))
    })
};
export function getMoleculeTypeColorThemeParams(ctx) {
    return MoleculeTypeColorThemeParams; // TODO return copy
}
export function moleculeTypeColor(colorMap, unit, element) {
    const moleculeType = getElementMoleculeType(unit, element);
    switch (moleculeType) {
        case MoleculeType.Water: return colorMap.water;
        case MoleculeType.Ion: return colorMap.ion;
        case MoleculeType.Protein: return colorMap.protein;
        case MoleculeType.RNA: return colorMap.RNA;
        case MoleculeType.DNA: return colorMap.DNA;
        case MoleculeType.PNA: return colorMap.PNA;
        case MoleculeType.Saccharide: return colorMap.saccharide;
    }
    return DefaultMoleculeTypeColor;
}
export function MoleculeTypeColorTheme(ctx, props) {
    const colorMap = getAdjustedColorMap(props.colors.name === 'default' ? MoleculeTypeColors : props.colors.params, props.saturation, props.lightness);
    function color(location) {
        if (StructureElement.Location.is(location)) {
            return moleculeTypeColor(colorMap, location.unit, location.element);
        }
        else if (Bond.isLocation(location)) {
            return moleculeTypeColor(colorMap, location.aUnit, location.aUnit.elements[location.aIndex]);
        }
        return DefaultMoleculeTypeColor;
    }
    return {
        factory: MoleculeTypeColorTheme,
        granularity: 'group',
        color,
        props,
        description: Description,
        legend: TableLegend(Object.keys(colorMap).map(name => {
            return [name, colorMap[name]];
        }).concat([['Other/unknown', DefaultMoleculeTypeColor]]))
    };
}
export const MoleculeTypeColorThemeProvider = {
    name: 'molecule-type',
    label: 'Molecule Type',
    category: ColorThemeCategory.Residue,
    factory: MoleculeTypeColorTheme,
    getParams: getMoleculeTypeColorThemeParams,
    defaultValues: PD.getDefaultValues(MoleculeTypeColorThemeParams),
    isApplicable: (ctx) => !!ctx.structure
};
